import {
  Archive,
  ArrowLeft,
  BookOpen,
  Check,
  ChevronRight,
  Clock3,
  Download,
  FileText,
  Headphones,
  House,
  Languages,
  LibraryBig,
  Mic,
  MoreHorizontal,
  Pause,
  PenLine,
  Play,
  Plus,
  RotateCcw,
  Save,
  Search,
  ShieldCheck,
  Sparkles,
  Square,
  Upload,
  Volume2,
} from "lucide-react";
import {
  useEffect,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import type {
  AssistanceLevel,
  CreativeProject,
  Language,
  ManuscriptVersion,
  NewProjectDraft,
} from "./domain";
import { emptyProjectDraft } from "./seed";
import {
  createBackup,
  localAssistance,
  simulatedStoryboard,
  simulatedTranscription,
  localLanguageEditions,
} from "./services";
import { projectRepository, readBackupFile } from "./storage";

const languageName = (language: Language) =>
  language === "si-LK" ? "Sinhala" : language === "en-GB" ? "English" : "Not sure yet";

const NEW_WORK_KEY = "author-studio-unfinished-work";
const OLD_NEW_WORK_KEY = "author-studio-new-project";

function migrateDraft(): NewProjectDraft {
  const fresh = emptyProjectDraft();
  try {
    const current = localStorage.getItem(NEW_WORK_KEY);
    if (current) {
      const parsed = JSON.parse(current) as Partial<NewProjectDraft>;
      return {
        ...fresh,
        ...parsed,
        collaborationAnswers: {
          ...fresh.collaborationAnswers,
          ...parsed.collaborationAnswers,
        },
      };
    }
    const old = sessionStorage.getItem(OLD_NEW_WORK_KEY);
    if (!old) return fresh;
    const parsed = JSON.parse(old) as Record<string, unknown>;
    const migrated: NewProjectDraft = {
      ...fresh,
      idea: typeof parsed.idea === "string" ? parsed.idea : "",
      workType: typeof parsed.workType === "string" ? parsed.workType as NewProjectDraft["workType"] : fresh.workType,
      audience: typeof parsed.audience === "string" ? parsed.audience : "",
      tones: typeof parsed.tone === "string" && parsed.tone ? [parsed.tone] : [],
      feelings: Array.isArray(parsed.feelings) ? parsed.feelings.filter((item): item is string => typeof item === "string") : [],
      assistanceLevel: parsed.assistanceLevel === "Create with me" ? "Build it with me" : (parsed.assistanceLevel as AssistanceLevel ?? fresh.assistanceLevel),
      originalLanguage: parsed.originalLanguage === "si-LK" || parsed.originalLanguage === "en-GB" ? parsed.originalLanguage : "und",
      languageManuallySelected: parsed.originalLanguage === "si-LK" || parsed.originalLanguage === "en-GB",
      preserveWording: typeof parsed.preserveWording === "boolean" ? parsed.preserveWording : true,
      preserveCulture: typeof parsed.preserveCulture === "boolean" ? parsed.preserveCulture : true,
      collaborationAnswers: fresh.collaborationAnswers,
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(NEW_WORK_KEY, JSON.stringify(migrated));
    sessionStorage.removeItem(OLD_NEW_WORK_KEY);
    return migrated;
  } catch {
    return fresh;
  }
}

function readUnfinishedDraft() {
  try {
    const value = localStorage.getItem(NEW_WORK_KEY);
    if (!value) return null;
    const fresh = emptyProjectDraft();
    const parsed = JSON.parse(value) as Partial<NewProjectDraft>;
    return {
      ...fresh,
      ...parsed,
      collaborationAnswers: { ...fresh.collaborationAnswers, ...parsed.collaborationAnswers },
    };
  } catch {
    return null;
  }
}

const formatTime = (value: string) =>
  new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));

const makeTitle = (idea: string) => {
  const firstLine = idea.trim().split(/[.!?\n]/)[0] || "My new work";
  if (firstLine.length <= 46) return firstLine;
  const shortened = firstLine.slice(0, 46).replace(/\s+\S*$/, "").trim();
  return `${shortened || firstLine.slice(0, 43)}…`;
};

function RouteFocus() {
  const location = useLocation();
  useEffect(() => {
    const heading = document.querySelector<HTMLElement>("main h1");
    if (heading) {
      heading.tabIndex = -1;
      heading.focus();
    }
  }, [location.pathname]);
  return null;
}

function Modal({
  title,
  children,
  onClose,
  actions,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  actions: ReactNode;
}) {
  const titleId = useId();
  const backdropRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const backdrop = backdropRef.current;
    const background = [...document.body.children]
      .filter((element): element is HTMLElement => element instanceof HTMLElement && element !== backdrop)
      .map((element) => ({
        element,
        inert: element.inert,
        ariaHidden: element.getAttribute("aria-hidden"),
      }));
    background.forEach(({ element }) => {
      element.inert = true;
      element.setAttribute("aria-hidden", "true");
    });
    closeRef.current?.focus();
    const containFocus = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || !backdrop) return;
      const focusable = [...backdrop.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )];
      if (!focusable.length) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable.at(-1)!;
      if (event.shiftKey && (document.activeElement === first || !backdrop.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !backdrop.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", containFocus);
    return () => {
      document.removeEventListener("keydown", containFocus);
      background.forEach(({ element, inert, ariaHidden }) => {
        element.inert = inert;
        if (ariaHidden === null) element.removeAttribute("aria-hidden");
        else element.setAttribute("aria-hidden", ariaHidden);
      });
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);
  return createPortal(
    <div ref={backdropRef} className="dialog-backdrop" role="presentation">
      <section className="dialog" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <h2 id={titleId}>{title}</h2>
        {children}
        <div className="dialog-actions">
          <button ref={closeRef} className="button ghost" onClick={onClose}>Cancel</button>
          {actions}
        </div>
      </section>
    </div>,
    document.body,
  );
}

function App() {
  const [projects, setProjects] = useState<CreativeProject[]>([]);
  const [ready, setReady] = useState(false);
  const [saveMessage, setSaveMessage] = useState("Saved on this device");
  const [storageError, setStorageError] = useState("");

  useEffect(() => {
    projectRepository.list()
      .then(setProjects)
      .catch(() => setStorageError("Your saved work could not be opened. Refresh the page or restore from a backup."))
      .finally(() => setReady(true));
  }, []);

  const saveProject = async (project: CreativeProject) => {
    setSaveMessage("Saving…");
    const updated = { ...project, updatedAt: new Date().toISOString() };
    setProjects((items) => items.map((item) => (item.id === updated.id ? updated : item)));
    try {
      await projectRepository.save(updated);
      setSaveMessage("Saved on this device");
      setStorageError("");
      return true;
    } catch {
      setSaveMessage("Not saved");
      setStorageError("This change could not be saved. Please back up your work and try again.");
      return false;
    }
  };

  const addProject = async (project: CreativeProject) => {
    setProjects((items) => [project, ...items]);
    try {
      await projectRepository.save(project);
      return true;
    } catch {
      setProjects((items) => items.filter((item) => item.id !== project.id));
      setStorageError("This new work could not be saved. Please try again.");
      return false;
    }
  };

  const replaceProjects = async (items: CreativeProject[]) => {
    const previous = projects;
    setProjects(items);
    try {
      await projectRepository.replaceAll(items);
      return true;
    } catch {
      setProjects(previous);
      setStorageError("Your backup could not be restored. Your existing work has been kept.");
      return false;
    }
  };

  if (!ready) {
    return (
      <main className="loading-shell">
        <div className="brand-mark" aria-hidden="true">A</div>
        <p>Opening your creative studio…</p>
      </main>
    );
  }

  return (
    <>
      <RouteFocus />
      {storageError && <div className="global-error" role="alert">{storageError}</div>}
      <Routes>
      <Route path="/" element={<HomePage projects={projects} />} />
      <Route
        path="/library"
        element={
          <LibraryPage
            projects={projects}
            replaceProjects={replaceProjects}
            saveProject={saveProject}
            addProject={addProject}
          />
        }
      />
      <Route path="/new" element={<Navigate to="/new/capture" replace />} />
      <Route path="/new/:step" element={<NewProject addProject={addProject} />} />
      <Route
        path="/project/:projectId/settings"
        element={<WorkSettings projects={projects} saveProject={saveProject} />}
      />
      <Route
        path="/project/:projectId"
        element={<ProjectOverview projects={projects} saveMessage={saveMessage} saveProject={saveProject} />}
      />
      <Route
        path="/project/:projectId/edit"
        element={<Editor projects={projects} saveMessage={saveMessage} saveProject={saveProject} />}
      />
      <Route
        path="/project/:projectId/versions"
        element={<Versions projects={projects} saveProject={saveProject} addProject={addProject} />}
      />
      <Route
        path="/project/:projectId/editions"
        element={<Editions projects={projects} saveProject={saveProject} />}
      />
      <Route path="/project/:projectId/listen" element={<Listen projects={projects} />} />
      <Route
        path="/project/:projectId/storyboard"
        element={<Storyboard projects={projects} saveProject={saveProject} />}
      />
      <Route
        path="/archive"
        element={<ArchivePage projects={projects} saveProject={saveProject} />}
      />
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function BrandHeader({ backTo, saveMessage }: { backTo?: string; saveMessage?: string }) {
  return (
    <header className="app-header">
      <div className="header-inner">
        {backTo ? (
          <Link className="icon-link" to={backTo} aria-label="Go back">
            <ArrowLeft size={24} />
          </Link>
        ) : (
          <Link to="/library" className="brand">
            <span className="brand-mark" aria-hidden="true">A</span>
            <span>Author Studio</span>
          </Link>
        )}
        {saveMessage && (
          <span className="save-status" role="status">
            <Check size={17} /> {saveMessage}
          </span>
        )}
      </div>
    </header>
  );
}

function StudioNavigation({ current }: { current: "home" | "stories" }) {
  return (
    <>
      <header className="app-header home-header">
        <div className="header-inner">
          <Link to="/" className="brand">
            <span className="brand-mark" aria-hidden="true">A</span>
            <span>Author Studio</span>
          </Link>
          <nav className="desktop-navigation" aria-label="Main navigation">
            <Link to="/" aria-current={current === "home" ? "page" : undefined}>Home</Link>
            <Link to="/library" aria-current={current === "stories" ? "page" : undefined}>My stories</Link>
          </nav>
        </div>
      </header>
      <nav className="mobile-navigation" aria-label="Mobile navigation">
        <Link to="/" aria-current={current === "home" ? "page" : undefined}><House />Home</Link>
        <Link to="/library" aria-current={current === "stories" ? "page" : undefined}><LibraryBig />My stories</Link>
        <Link to="/new/capture"><Plus />New</Link>
      </nav>
    </>
  );
}

const helpOptions = [
  "Continue my story",
  "Write the rest",
  "Improve my writing",
  "Make it funnier",
  "Make it more moving",
  "Write an ending",
  "Translate my story",
] as const;

function AssistanceMenu({ project, onClose }: { project: CreativeProject; onClose: () => void }) {
  const navigate = useNavigate();
  const [comingNext, setComingNext] = useState<string | null>(null);
  const choose = (option: typeof helpOptions[number]) => {
    if (option === "Continue my story") {
      navigate(`/project/${project.id}/edit`);
      return;
    }
    if (option === "Translate my story") {
      navigate(`/project/${project.id}/editions`);
      return;
    }
    setComingNext(option);
  };
  return (
    <Modal title={comingNext ? `${comingNext} is coming next` : "How would you like help?"} onClose={onClose} actions={null}>
      {comingNext ? (
        <div className="coming-next">
          <Sparkles />
          <p>This zero-cost help option is not connected yet. Your story has not changed.</p>
          <button className="button secondary" onClick={() => setComingNext(null)}>Choose different help</button>
        </div>
      ) : (
        <>
          <p className="help-story-title">For “{project.title}”</p>
          <div className="assistance-menu">
            {helpOptions.map((option) => (
              <button key={option} onClick={() => choose(option)}>
                <span>{option}</span><ChevronRight />
              </button>
            ))}
          </div>
          <p className="compatibility-note">Some help choices are demonstrations while zero-cost assisted writing is being prepared.</p>
        </>
      )}
    </Modal>
  );
}

function HomePage({ projects }: { projects: CreativeProject[] }) {
  const active = projects
    .filter((project) => project.status === "active")
    .sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt));
  const latest = active[0];
  const unfinished = readUnfinishedDraft();
  const [showHelp, setShowHelp] = useState(false);
  return (
    <>
      <StudioNavigation current="home" />
      <main className="page home-page">
        <section className="home-introduction">
          <p className="eyebrow">Your writing space</p>
          <h1>What would you like to do?</h1>
        </section>

        {unfinished?.idea.trim() && (
          <section className="home-unfinished" aria-labelledby="home-unfinished-heading">
            <div>
              <p className="eyebrow">Saved unfinished idea</p>
              <h2 id="home-unfinished-heading">Continue my unfinished idea</h2>
              <p>{unfinished.idea.slice(0, 140)}{unfinished.idea.length > 140 ? "…" : ""}</p>
            </div>
            <Link className="button secondary" to={`/new/${unfinished.stage}`}>Continue my unfinished idea</Link>
          </section>
        )}

        <section className="home-primary-actions" aria-label="Main actions">
          {latest ? (
            <div className="latest-story">
              <div>
                <p className="eyebrow">Your latest story</p>
                <h2>{latest.title}</h2>
                <p>{latest.workType} · {languageName(latest.originalLanguage)} · Saved {formatTime(latest.updatedAt)}</p>
              </div>
              <Link className="button primary home-dominant" to={`/project/${latest.id}/edit`}>
                <PenLine /> Continue “{latest.title}”
              </Link>
            </div>
          ) : (
            <div className="latest-story empty-latest">
              <h2>Your first story can begin here</h2>
              <p>Start with an idea, a memory, or a few words.</p>
            </div>
          )}
          <Link className="button start-story-action" to="/new/capture"><Plus /> Start a new story</Link>
        </section>

        <section className="home-secondary-actions" aria-label="More actions">
          <Link className="button ghost" to="/library"><LibraryBig /> Open another story</Link>
          <button className="button ghost" disabled={!latest} onClick={() => setShowHelp(true)}><Sparkles /> Get help with my story</button>
        </section>
      </main>
      {showHelp && latest && <AssistanceMenu project={latest} onClose={() => setShowHelp(false)} />}
    </>
  );
}

function MoreMenu({
  project,
  onArchive,
  onCopy,
  onSelect,
}: {
  project: CreativeProject;
  onArchive: () => void;
  onCopy: () => void;
  onSelect: () => void;
}) {
  return (
    <div className="more-menu" role="menu" aria-label={`More actions for ${project.title}`}>
      <Link role="menuitem" to={`/project/${project.id}/versions`} onClick={onSelect}>Earlier versions</Link>
      <Link role="menuitem" to={`/project/${project.id}/editions`} onClick={onSelect}>Language editions</Link>
      <Link role="menuitem" to={`/project/${project.id}/storyboard`} onClick={onSelect}>Comic storyboard</Link>
      <button role="menuitem" onClick={() => { onSelect(); onCopy(); }}>Make a copy</button>
      <button role="menuitem" onClick={() => { onSelect(); onArchive(); }}>Move to Archived stories</button>
    </div>
  );
}

function LibraryPage({
  projects,
  replaceProjects,
  saveProject,
  addProject,
}: {
  projects: CreativeProject[];
  replaceProjects: (items: CreativeProject[]) => Promise<boolean>;
  saveProject: (project: CreativeProject) => Promise<boolean>;
  addProject: (project: CreativeProject) => Promise<boolean>;
}) {
  const active = projects
    .filter((project) => project.status === "active")
    .sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt));
  const [search, setSearch] = useState("");
  const filtered = active.filter((project) => project.title.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()));
  const showSearch = active.length >= 6;
  const fileRef = useRef<HTMLInputElement>(null);
  const [restoreFile, setRestoreFile] = useState<File | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<CreativeProject | null>(null);
  const [unfinished, setUnfinished] = useState<NewProjectDraft | null>(() => readUnfinishedDraft());
  const [confirmStartOver, setConfirmStartOver] = useState(false);
  const [helpTarget, setHelpTarget] = useState<CreativeProject | null>(null);
  const [moreTarget, setMoreTarget] = useState<string | null>(null);
  const moreButtonRefs = useRef(new Map<string, HTMLButtonElement>());
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!moreTarget) return;
    const closeOnPointerDown = (event: PointerEvent) => {
      const target = event.target instanceof Element ? event.target.closest<HTMLElement>("[data-more-project]") : null;
      if (target?.dataset.moreProject !== moreTarget) setMoreTarget(null);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      const trigger = moreButtonRefs.current.get(moreTarget);
      setMoreTarget(null);
      trigger?.focus();
    };
    document.addEventListener("pointerdown", closeOnPointerDown);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnPointerDown);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [moreTarget]);

  const makeCopy = async (project: CreativeProject) => {
    const source = project.versions.at(-1)!;
    const now = new Date().toISOString();
    const copy: CreativeProject = {
      ...project,
      id: crypto.randomUUID(),
      title: `${project.title} — Copy`,
      versions: [{
        ...source,
        id: crypto.randomUUID(),
        number: 1,
        name: "Copied from latest version",
        createdAt: now,
        reason: "initial",
        basedOnVersionId: undefined,
      }],
      editions: [],
      storyboards: [],
      status: "active",
      updatedAt: now,
    };
    if (await addProject(copy)) setMessage(`A copy of “${project.title}” was created.`);
    setMoreTarget(null);
  };

  const downloadBackup = () => {
    const blob = new Blob([JSON.stringify(createBackup(projects, unfinished ?? undefined), null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `author-studio-backup-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    setMessage("Your backup was downloaded to this device.");
  };

  const chooseRestore = (event: ChangeEvent<HTMLInputElement>) => {
    setRestoreFile(event.target.files?.[0] ?? null);
  };

  const confirmRestore = async () => {
    if (!restoreFile) return;
    try {
      const backup = await readBackupFile(restoreFile);
      const restored = await replaceProjects(backup.projects);
      if (!restored) return;
      if (backup.unfinishedWork) {
        localStorage.setItem(NEW_WORK_KEY, JSON.stringify(backup.unfinishedWork));
        setUnfinished(backup.unfinishedWork);
      }
      setRestoreFile(null);
      if (fileRef.current) fileRef.current.value = "";
      setMessage("Your projects were restored from the backup.");
    } catch {
      setMessage("That file could not be restored. Please choose an Author Studio backup.");
    }
  };

  return (
    <>
      <StudioNavigation current="stories" />
      <main className="page library-page">
        <section className="page-heading split-heading">
          <div>
            <p className="eyebrow">Your writing</p>
            <h1>My stories</h1>
            <p>Choose a story to continue, or begin something new.</p>
          </div>
          <Link className="button primary" to="/new/capture">
            <Plus size={21} /> Start a new story
          </Link>
        </section>

        {unfinished && unfinished.idea.trim() && (
          <section className="unfinished-card" aria-labelledby="unfinished-heading">
            <div>
              <p className="eyebrow">Saved on this device</p>
              <h2 id="unfinished-heading">Unfinished idea</h2>
              <p>{unfinished.idea.slice(0, 150)}{unfinished.idea.length > 150 ? "…" : ""}</p>
            </div>
            <div className="tool-actions">
              <Link className="button primary" to={`/new/${unfinished.stage}`}>Continue</Link>
              <button className="button ghost" onClick={() => setConfirmStartOver(true)}>Start over</button>
            </div>
          </section>
        )}
        {message && <p className="notice-message" role="status">{message}</p>}

        {showSearch && (
          <label className="story-search">
            <span className="visually-hidden">Search my stories</span>
            <Search />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search my stories" />
          </label>
        )}

        <section aria-labelledby="stories-heading">
          <div className="section-heading">
            <h2 id="stories-heading">All stories</h2>
            <span>{active.length} {active.length === 1 ? "story" : "stories"}</span>
          </div>
          <div className="story-list">
            {filtered.map((project) => (
              <article className="story-row" key={project.id}>
                <Link to={`/project/${project.id}/edit`} className="story-row-main">
                  <span className="story-row-icon" aria-hidden="true"><BookOpen /></span>
                  <span>
                    <strong>{project.title}</strong>
                    <small>{project.workType} · {languageName(project.originalLanguage)}</small>
                  </span>
                  <span className="story-saved">Saved {formatTime(project.updatedAt)}</span>
                </Link>
                <div className="story-row-actions">
                  <Link className="button primary" to={`/project/${project.id}/edit`}>Continue</Link>
                  <button className="button secondary" onClick={() => setHelpTarget(project)}>Get help</button>
                  <div className="more-wrapper" data-more-project={project.id}>
                    <button
                      ref={(element) => {
                        if (element) moreButtonRefs.current.set(project.id, element);
                        else moreButtonRefs.current.delete(project.id);
                      }}
                      className="button ghost"
                      aria-haspopup="menu"
                      aria-expanded={moreTarget === project.id}
                      aria-controls={`more-${project.id}`}
                      onClick={() => setMoreTarget((value) => value === project.id ? null : project.id)}
                    >
                      More <MoreHorizontal />
                    </button>
                    {moreTarget === project.id && (
                      <div id={`more-${project.id}`}>
                        <MoreMenu
                          project={project}
                          onSelect={() => setMoreTarget(null)}
                          onCopy={() => void makeCopy(project)}
                          onArchive={() => {
                            setArchiveTarget(project);
                            setMoreTarget(null);
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </article>
            ))}
            {!filtered.length && <p className="empty-search">No stories match that search.</p>}
          </div>
        </section>

        <section className="library-tools">
          <div>
            <h2>Keep your work safe</h2>
            <p>Your work is saved on this device.</p>
          </div>
          <div className="tool-actions">
            <button className="button secondary" onClick={downloadBackup}>
              <Download size={20} /> Back up my work
            </button>
            <button className="button secondary" onClick={() => fileRef.current?.click()}>
              <Upload size={20} /> Restore my work
            </button>
            <input
              ref={fileRef}
              className="visually-hidden"
              type="file"
              accept=".json,application/json"
              onChange={chooseRestore}
            />
            <Link className="button ghost" to="/archive">
              <Archive size={20} /> Archived stories
            </Link>
          </div>
        </section>

        {restoreFile && (
          <Modal
            title="Restore from this backup?"
            onClose={() => {
              setRestoreFile(null);
              if (fileRef.current) fileRef.current.value = "";
            }}
            actions={<button className="button primary" onClick={() => void confirmRestore()}>Restore my work</button>}
          >
              <div className="dialog-icon"><Upload /></div>
              <p>
                This will replace the projects currently saved in this browser with
                the projects in <strong>{restoreFile.name}</strong>.
              </p>
              <p className="warning-copy">Back up your current work first if you may need it later.</p>
          </Modal>
        )}
        {archiveTarget && (
          <Modal
            title={`Archive “${archiveTarget.title}”?`}
            onClose={() => setArchiveTarget(null)}
            actions={
              <button
                className="button primary"
                onClick={() => {
                  void saveProject({ ...archiveTarget, status: "archived" });
                  setArchiveTarget(null);
                }}
              >
                Move to Archived stories
              </button>
            }
          >
            <p>You can restore this story later. Nothing will be permanently deleted.</p>
          </Modal>
        )}
        {confirmStartOver && (
          <Modal
            title="Start over?"
            onClose={() => setConfirmStartOver(false)}
            actions={
              <button
                className="button danger"
                onClick={() => {
                  localStorage.removeItem(NEW_WORK_KEY);
                  setUnfinished(null);
                  setConfirmStartOver(false);
                }}
              >
                Replace unfinished work
              </button>
            }
          >
            <p>This will remove the unfinished setup. Your existing stories will not be affected.</p>
          </Modal>
        )}
      </main>
      {helpTarget && <AssistanceMenu project={helpTarget} onClose={() => setHelpTarget(null)} />}
    </>
  );
}

const wizardSteps = ["capture", "assistance", "direction", "review"] as const;
const toneOptions = ["Warm", "Funny", "Gentle", "Playful", "Serious", "Emotional", "Moving", "Mysterious", "Suspenseful", "Magical", "Romantic", "Dark", "Inspirational", "Not sure yet"];
const feelingOptions = ["Happy", "Moved", "Amused", "Hopeful", "Curious", "Excited", "Comforted", "Surprised", "Inspired", "Sad", "Frightened", "Nostalgic", "Thoughtful", "Not sure yet"];

function appendWriting(existing: string, addition: string) {
  const clean = addition.trim();
  if (!clean) return existing;
  return existing.trim() ? `${existing.trimEnd()}\n\n${clean}` : clean;
}

function suggestLanguage(text: string): Language {
  if (/[\u0D80-\u0DFF]/u.test(text)) return "si-LK";
  if (/[A-Za-z]{4}/.test(text)) return "en-GB";
  return "und";
}

function NewProject({ addProject }: { addProject: (project: CreativeProject) => Promise<boolean> }) {
  const { step = "capture" } = useParams();
  const navigate = useNavigate();
  const [draft, setDraft] = useState<NewProjectDraft>(migrateDraft);
  const saveMessage = "Saved on this device";
  const index = Math.max(0, wizardSteps.indexOf(step as typeof wizardSteps[number]));
  const updateDraft = (changes: Partial<NewProjectDraft>) => {
    setDraft((value) => {
      const saved = { ...value, ...changes, savedAt: new Date().toISOString() };
      localStorage.setItem(NEW_WORK_KEY, JSON.stringify(saved));
      return saved;
    });
  };
  const next = () => {
    const nextStage = wizardSteps[Math.min(index + 1, wizardSteps.length - 1)];
    updateDraft({ stage: nextStage });
    navigate(`/new/${nextStage}`);
  };
  const back = () => index === 0 ? navigate("/library") : navigate(`/new/${wizardSteps[index - 1]}`);

  useEffect(() => {
    if (!wizardSteps.includes(step as typeof wizardSteps[number])) {
      navigate("/new/capture", { replace: true });
    }
  }, [navigate, step]);

  const finish = async () => {
    const now = new Date().toISOString();
    const manuscript = await localAssistance.createDraft(draft);
    const project: CreativeProject = {
      id: crypto.randomUUID(),
      title: makeTitle(draft.idea),
      workType: draft.workType,
      status: "active",
      originalLanguage: draft.originalLanguage,
      creationMethod: "write",
      audience: draft.audience || "Not sure yet",
      tone: [...draft.tones, draft.customTone].filter(Boolean).join(", ") || "Not sure yet",
      tones: draft.tones,
      feelings: draft.feelings,
      customTone: draft.customTone,
      customFeeling: draft.customFeeling,
      emotionalDirection: draft.emotionalDirection,
      assistanceLevel: draft.assistanceLevel,
      preserveWording: draft.preserveWording,
      preserveCulture: draft.preserveCulture,
      collaborationAnswers: draft.collaborationAnswers,
      originalInput: draft.idea,
      workingText: manuscript,
      versions: [{
        id: crypto.randomUUID(),
        number: 1,
        name: "First draft",
        text: manuscript,
        createdAt: now,
        reason: "initial",
      }],
      editions: [],
      storyboards: [],
      updatedAt: now,
    };
    const created = await addProject(project);
    if (!created) return;
    localStorage.removeItem(NEW_WORK_KEY);
    sessionStorage.removeItem(OLD_NEW_WORK_KEY);
    navigate(`/project/${project.id}/edit?new=true`);
  };

  return (
    <>
      <BrandHeader />
      <main className="wizard">
        <button className="back-button" onClick={back}><ArrowLeft /> Back</button>
        <div
          className="progress"
          role="progressbar"
          aria-label="New work progress"
          aria-valuemin={1}
          aria-valuemax={wizardSteps.length}
          aria-valuenow={index + 1}
          aria-valuetext={`Step ${index + 1} of ${wizardSteps.length}`}
        >
          <span style={{ width: `${((index + 1) / wizardSteps.length) * 100}%` }} />
        </div>
        <p className="step-count">Step {index + 1} of {wizardSteps.length}</p>
        {step === "capture" && (
          <CaptureStep draft={draft} updateDraft={updateDraft} saveMessage={saveMessage} onContinue={next} />
        )}
        {step === "assistance" && (
          <WizardQuestion title="How would you like Author Studio to help?" hint="Your original contribution will always be kept.">
            <div className="stacked-choices">
              {([
                ["Keep my words", "Tidy only obvious mistakes."],
                ["Help me improve it", "Improve the writing while keeping my meaning."],
                ["Develop my ideas", "Add detail, scenes, or explanations."],
                ["Create a first draft", "Turn my ideas into a complete draft."],
                ["Build it with me", "Ask me simple questions and develop it step by step."],
              ] as [AssistanceLevel, string][]).map(([title, description]) => (
                <Choice key={title} selected={draft.assistanceLevel === title} title={title} description={description} onClick={() => updateDraft({ assistanceLevel: title })} />
              ))}
            </div>
            {draft.assistanceLevel === "Build it with me" && (
              <section className="collaboration-questions">
                <h2>Let’s build it together</h2>
                <p>Answer what you can. Your answers are saved with this unfinished work.</p>
                {([
                  ["centre", "Who or what is at the centre of the work?"],
                  ["change", "What happens or changes?"],
                  ["challenge", "What is the main challenge, question, or message?"],
                  ["ending", "How would you like it to end?"],
                  ["preserveExactly", "Is there anything that must remain exactly as you described it?"],
                ] as const).map(([key, label]) => (
                  <label className="field-label" key={key}>
                    {label}
                    <textarea
                      value={draft.collaborationAnswers[key]}
                      onChange={(event) => updateDraft({
                        collaborationAnswers: {
                          ...draft.collaborationAnswers,
                          [key]: event.target.value,
                        },
                      })}
                    />
                  </label>
                ))}
              </section>
            )}
            <div className="wizard-actions"><button className="button primary" onClick={next}>Continue <ChevronRight /></button></div>
          </WizardQuestion>
        )}
        {step === "direction" && (
          <CreativeDirection draft={draft} updateDraft={updateDraft} onContinue={next} />
        )}
        {step === "review" && (
          <ReviewDraft draft={draft} navigate={navigate} finish={finish} />
        )}
      </main>
    </>
  );
}

function WizardQuestion({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return <section className="wizard-question"><p className="eyebrow">Create a new work</p><h1>{title}</h1>{hint && <p className="question-hint">{hint}</p>}{children}</section>;
}

function Choice({ selected, icon, title, description, onClick }: { selected: boolean; icon?: ReactNode; title: string; description: string; onClick: () => void }) {
  return (
    <button className={`choice ${selected ? "selected" : ""}`} aria-pressed={selected} onClick={onClick}>
      {icon && <span className="choice-icon">{icon}</span>}
      <span><strong>{title}</strong>{description && <small>{description}</small>}</span>
      <span className="choice-check">{selected && <Check />}</span>
    </button>
  );
}

function CaptureStep({ draft, updateDraft, saveMessage, onContinue }: { draft: NewProjectDraft; updateDraft: (changes: Partial<NewProjectDraft>) => void; saveMessage: string; onContinue: () => void }) {
  const navigate = useNavigate();
  const [recordingState, setRecordingState] = useState<"idle" | "requesting" | "recording">("idle");
  const [elapsed, setElapsed] = useState(0);
  const [message, setMessage] = useState("");
  const [showExisting, setShowExisting] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const [voiceDisclosure, setVoiceDisclosure] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const streamRef = useRef<MediaStream | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const ideaRef = useRef(draft.idea);

  useEffect(() => {
    ideaRef.current = draft.idea;
  }, [draft.idea]);

  useEffect(() => () => {
    window.clearInterval(timer.current);
    streamRef.current?.getTracks().forEach((track) => track.stop());
  }, []);

  const startRecording = async () => {
    setRecordingState("requesting");
    setMessage("Requesting microphone…");
    setVoiceDisclosure(true);
    try {
      if (navigator.mediaDevices?.getUserMedia) {
        const stream = await Promise.race([
          navigator.mediaDevices.getUserMedia({ audio: true }),
          new Promise<never>((_, reject) => window.setTimeout(() => reject(new Error("permission-timeout")), 8000)),
        ]);
        streamRef.current = stream;
      } else {
        throw new Error("unsupported");
      }
      setRecordingState("recording");
      setElapsed(0);
      setMessage("Listening. This demonstration stops after five minutes.");
      timer.current = window.setInterval(() => {
        setElapsed((value) => {
          if (value >= 299) {
            window.clearInterval(timer.current);
            streamRef.current?.getTracks().forEach((track) => track.stop());
            setRecordingState("idle");
            void simulatedTranscription.simulatedTranscript(draft.originalLanguage).then((text) => {
              const idea = appendWriting(ideaRef.current, text);
              ideaRef.current = idea;
              updateDraft({ idea });
            });
            setMessage("Listening stopped at the five-minute demonstration limit. Everything captured so far has been kept.");
            return 300;
          }
          if (value === 269) setMessage("Thirty seconds remaining in this demonstration recording.");
          return value + 1;
        });
      }, 1000);
    } catch {
      setRecordingState("idle");
      setMessage("Microphone unavailable — type instead");
    }
  };

  const stopRecording = async () => {
    window.clearInterval(timer.current);
    streamRef.current?.getTracks().forEach((track) => track.stop());
    setRecordingState("idle");
    const transcript = await simulatedTranscription.simulatedTranscript(draft.originalLanguage);
    const idea = appendWriting(ideaRef.current, transcript);
    ideaRef.current = idea;
    updateDraft({ idea });
    setMessage("Example voice-to-text was added. Review and change any words.");
  };

  const changeIdea = (idea: string) => {
    ideaRef.current = idea;
    updateDraft({ idea });
  };

  const importText = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".txt") && file.type !== "text/plain") {
      setMessage("We could not add this file. Choose a plain-text file or paste the writing instead.");
      return;
    }
    try {
      const text = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(reader.error);
        reader.onload = () => resolve(String(reader.result ?? ""));
        reader.readAsText(file);
      });
      changeIdea(appendWriting(ideaRef.current, text));
      setMessage("Writing from the file was added.");
    } catch {
      setMessage("We could not read this file. Paste the writing instead.");
    } finally {
      event.target.value = "";
    }
  };

  return (
    <WizardQuestion title="Tell us what you have in mind" hint="Start anywhere. Type, speak, or add writing you already have.">
      <label className="field-label" htmlFor="idea">Your idea or writing</label>
      <textarea
        id="idea"
        className="idea-area"
        value={draft.idea}
        onChange={(event) => changeIdea(event.target.value)}
        onBlur={() => {
          if (!draft.languageManuallySelected) updateDraft({ originalLanguage: suggestLanguage(ideaRef.current) });
        }}
        placeholder="Type an idea, some notes, or writing you already have…"
      />
      <div className="composer-toolbar">
        {recordingState === "recording" ? (
          <button className="button recording-button stop" onClick={() => void stopRecording()}><Square /> Stop</button>
        ) : (
          <button className="button recording-button" disabled={recordingState === "requesting"} onClick={() => void startRecording()}><Mic /> {recordingState === "requesting" ? "Requesting microphone…" : "Start speaking"}</button>
        )}
        {recordingState === "recording" && <strong>Listening · {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, "0")} / 5:00</strong>}
        <button className="button secondary" aria-expanded={showExisting} onClick={() => setShowExisting((value) => !value)}><FileText /> Add existing writing</button>
        <span className="save-status" role="status"><Check size={17} /> {saveMessage}</span>
      </div>
      {voiceDisclosure && <p className="compatibility-note">Voice-to-text is demonstrated in this early version. You can review and change the example text.</p>}
      {message && <p className="recording-message" role="status">{message}</p>}
      {showExisting && (
        <section className="disclosure-panel">
          <h2>Paste my writing</h2>
          {draft.idea.trim() && <p>This writing will be added to what is already here.</p>}
          <label className="field-label" htmlFor="paste-writing">Writing to add</label>
          <textarea id="paste-writing" value={pasteText} onChange={(event) => setPasteText(event.target.value)} />
          <div className="panel-actions">
            <button className="button primary" disabled={!pasteText.trim()} onClick={() => { changeIdea(appendWriting(ideaRef.current, pasteText)); setPasteText(""); }}>Add to my idea</button>
            <button className="button secondary" onClick={() => fileRef.current?.click()}>Choose a text file</button>
            <input ref={fileRef} className="visually-hidden" type="file" accept=".txt,text/plain" onChange={(event) => void importText(event)} />
          </div>
          <p className="privacy-note"><ShieldCheck size={18} /> Writing from this device stays in this browser and is not uploaded.</p>
        </section>
      )}
      <section className="optional-details">
        <button className="disclosure-button" aria-expanded={showDetails} onClick={() => setShowDetails((value) => !value)}>A few details — optional <ChevronRight /></button>
        {showDetails && (
          <div className="disclosure-panel">
            <fieldset><legend>What kind of work might this be?</legend><div className="pill-grid">{(["Fiction", "Non-fiction", "Children's story", "Not sure yet"] as const).map((item) => <TogglePill key={item} label={item} selected={draft.workType === item} onClick={() => updateDraft({ workType: item })} />)}</div></fieldset>
            <fieldset><legend>What language are you beginning in?</legend><div className="pill-grid"><TogglePill label="සිංහල — Sinhala" selected={draft.originalLanguage === "si-LK"} onClick={() => updateDraft({ originalLanguage: "si-LK", languageManuallySelected: true })} /><TogglePill label="English" selected={draft.originalLanguage === "en-GB"} onClick={() => updateDraft({ originalLanguage: "en-GB", languageManuallySelected: true })} /><TogglePill label="Not sure yet" selected={draft.originalLanguage === "und"} onClick={() => updateDraft({ originalLanguage: "und", languageManuallySelected: true })} /></div>{!draft.languageManuallySelected && draft.originalLanguage !== "und" && <p>Suggested from your writing: {languageName(draft.originalLanguage)}</p>}</fieldset>
          </div>
        )}
      </section>
      <div className="idea-preserved"><ShieldCheck /> Your original contribution is kept separately from assisted writing.</div>
      <div className="wizard-actions split-actions">
        <button className="button ghost" onClick={() => navigate("/library")}>Save and finish later</button>
        <button className="button primary" disabled={!draft.idea.trim()} onClick={onContinue}>Continue <ChevronRight /></button>
      </div>
    </WizardQuestion>
  );
}

function TogglePill({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
  return <button type="button" className={`toggle-pill ${selected ? "selected" : ""}`} aria-pressed={selected} onClick={onClick}>{label}</button>;
}

function toggleValue(values: string[], value: string) {
  return values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
}

function CreativeDirection({ draft, updateDraft, onContinue }: { draft: NewProjectDraft; updateDraft: (changes: Partial<NewProjectDraft>) => void; onContinue: () => void }) {
  const suggest = () => updateDraft({
    audience: draft.audience || "Everyone",
    tones: draft.tones.length ? draft.tones : ["Warm", "Inspirational"],
    feelings: draft.feelings.length ? draft.feelings : ["Hopeful", "Inspired"],
    suggestedFields: ["audience", "tones", "feelings"],
  });
  return (
    <WizardQuestion title="Add creative direction" hint="These choices are optional. Choose them now, let Author Studio suggest them, or change them later.">
      <button className="button secondary" onClick={suggest}><Sparkles /> Suggest these for me</button>
      <fieldset><legend>Who is it for?</legend><div className="pill-grid">{["Adults", "Families", "Young readers", "Everyone", "Not sure yet"].map((item) => <TogglePill key={item} label={item} selected={draft.audience === item} onClick={() => updateDraft({ audience: item, suggestedFields: draft.suggestedFields.filter((field) => field !== "audience") })} />)}</div></fieldset>
      <fieldset><legend>How should the work sound?</legend><div className="pill-grid">{toneOptions.map((item) => <TogglePill key={item} label={item} selected={draft.tones.includes(item)} onClick={() => updateDraft({ tones: toggleValue(draft.tones, item), suggestedFields: draft.suggestedFields.filter((field) => field !== "tones") })} />)}</div><label className="field-label" htmlFor="custom-tone">Add your own tone</label><input id="custom-tone" className="text-input" value={draft.customTone} onChange={(event) => updateDraft({ customTone: event.target.value })} /></fieldset>
      <fieldset><legend>How would you like readers to feel?</legend><div className="pill-grid">{feelingOptions.map((item) => <TogglePill key={item} label={item} selected={draft.feelings.includes(item)} onClick={() => updateDraft({ feelings: toggleValue(draft.feelings, item), suggestedFields: draft.suggestedFields.filter((field) => field !== "feelings") })} />)}</div><label className="field-label" htmlFor="custom-feeling">Add your own reader feeling</label><input id="custom-feeling" className="text-input" value={draft.customFeeling} onChange={(event) => updateDraft({ customFeeling: event.target.value })} /></fieldset>
      <label className="field-label" htmlFor="emotional-direction">Describe the feeling in your own words</label>
      <textarea id="emotional-direction" className="direction-area" value={draft.emotionalDirection} onChange={(event) => updateDraft({ emotionalDirection: event.target.value })} placeholder="The beginning should feel warm and funny, become emotional in the middle, and leave the reader hopeful at the end." />
      <div className="preserve-options"><strong>Keep what matters</strong><label><input type="checkbox" checked={draft.preserveWording} onChange={(event) => updateDraft({ preserveWording: event.target.checked })} /> Keep my original wording where possible</label><label><input type="checkbox" checked={draft.preserveCulture} onChange={(event) => updateDraft({ preserveCulture: event.target.checked })} /> Keep cultural words and expressions</label></div>
      <div className="wizard-actions split-actions"><button className="button ghost" onClick={onContinue}>Skip for now</button><button className="button primary" onClick={onContinue}>Review and create <ChevronRight /></button></div>
    </WizardQuestion>
  );
}

function ReviewDraft({ draft, navigate, finish }: { draft: NewProjectDraft; navigate: ReturnType<typeof useNavigate>; finish: () => Promise<void> }) {
  const summary = [
    ["Work type", draft.workType, "/new/capture"],
    ["Original language", languageName(draft.originalLanguage), "/new/capture"],
    ["Assistance", draft.assistanceLevel, "/new/assistance"],
    ["Audience", draft.audience || "Decide later", "/new/direction"],
    ["Tone", [...draft.tones, draft.customTone].filter(Boolean).join(", ") || "Decide later", "/new/direction"],
    ["Reader feelings", [...draft.feelings, draft.customFeeling].filter(Boolean).join(", ") || "Decide later", "/new/direction"],
    ["Emotional direction", draft.emotionalDirection || "Decide later", "/new/direction"],
    ["Keep what matters", `${draft.preserveWording ? "Keep wording" : "Wording may change"}; ${draft.preserveCulture ? "keep cultural expressions" : "no cultural-expression preference"}`, "/new/direction"],
  ];
  return (
    <WizardQuestion title="Review your choices" hint="You can change these settings now or after the draft is created.">
      <section className="review-preview"><h2>Your idea</h2><p>{draft.idea.slice(0, 220)}{draft.idea.length > 220 ? "…" : ""}</p></section>
      <dl className="review-list">{summary.map(([label, value, route]) => <div key={label}><dt>{label}</dt><dd>{value}{draft.suggestedFields.some((field) => label.toLowerCase().includes(field.replace("tones", "tone"))) && <small>Suggested by Author Studio</small>}</dd><button className="text-button" onClick={() => navigate(route)}>Change {label.toLowerCase()}</button></div>)}</dl>
      <div className="wizard-actions"><button className="button primary dominant" onClick={() => void finish()}><Sparkles /> Create my draft</button></div>
    </WizardQuestion>
  );
}

function useProject(projects: CreativeProject[]) {
  const { projectId } = useParams();
  return projects.find((project) => project.id === projectId);
}

function ProjectOverview({ projects, saveMessage, saveProject }: { projects: CreativeProject[]; saveMessage: string; saveProject: (project: CreativeProject) => Promise<boolean> }) {
  const project = useProject(projects);
  const navigate = useNavigate();
  const [showArchive, setShowArchive] = useState(false);
  if (!project) return <Navigate to="/library" replace />;
  const latest = project.versions.at(-1)!;
  return (
    <>
      <BrandHeader backTo="/library" saveMessage={saveMessage} />
      <main className="page overview-page">
        <section className="project-hero">
          <div>
            <div className="tag-row"><span className="tag">Master manuscript</span><span className="tag soft">{languageName(project.originalLanguage)} original</span></div>
            <h1>{project.title}</h1>
            <p>{project.workType} · {project.audience} · {project.tone}</p>
          </div>
          <Link className="button primary" to={`/project/${project.id}/edit`}><PenLine /> Continue writing</Link>
        </section>
        <div className="overview-layout">
          <section className="master-card">
            <div className="section-heading"><div><p className="eyebrow">Your main work</p><h2>Master manuscript</h2></div><FileText /></div>
            <p className="manuscript-preview">{project.workingText}</p>
            <div className="master-footer">
              <span><Clock3 /> Version {latest.number}: {latest.name}</span>
              <Link to={`/project/${project.id}/edit`}>Open manuscript <ChevronRight /></Link>
            </div>
          </section>
          <aside className="project-details">
            <h2>About this work</h2>
            <dl>
              <div><dt>Original language</dt><dd>{languageName(project.originalLanguage)}</dd></div>
              <div><dt>Help chosen</dt><dd>{project.assistanceLevel}</dd></div>
              <div><dt>Feelings</dt><dd>{project.feelings.join(", ")}</dd></div>
              <div><dt>Your wording</dt><dd>{project.preserveWording ? "Keep where possible" : "May be reshaped"}</dd></div>
            </dl>
          </aside>
        </div>
        <section className="connections">
          <div className="section-heading"><div><p className="eyebrow">Everything stays connected</p><h2>Versions, editions and adaptations</h2></div></div>
          <div className="connection-flow">
            <Link to={`/project/${project.id}/versions`} className="connection-card"><Clock3 /><span><strong>{project.versions.length} saved {project.versions.length === 1 ? "version" : "versions"}</strong><small>See and restore earlier writing</small></span><ChevronRight /></Link>
            <Link to={`/project/${project.id}/editions`} className="connection-card"><Languages /><span><strong>{project.editions.length ? `${project.editions.length} language ${project.editions.length === 1 ? "edition" : "editions"}` : "No language editions"}</strong><small>Linked to a master version</small></span><ChevronRight /></Link>
            <Link to={`/project/${project.id}/storyboard`} className="connection-card"><BookOpen /><span><strong>{project.storyboards.length ? `${project.storyboards.length} comic ${project.storyboards.length === 1 ? "storyboard" : "storyboards"}` : "No comic storyboards"}</strong><small>A separate creative adaptation</small></span><ChevronRight /></Link>
          </div>
        </section>
        <section className="quick-actions">
          <Link className="button secondary" to={`/project/${project.id}/settings`}><Sparkles /> Work settings</Link>
          <Link className="button secondary" to={`/project/${project.id}/listen`}><Headphones /> Listen to my work</Link>
          <button className="button ghost" onClick={() => setShowArchive(true)}><Archive /> Move to Archived works</button>
        </section>
        {showArchive && (
          <Modal
            title={`Archive “${project.title}”?`}
            onClose={() => setShowArchive(false)}
            actions={<button className="button primary" onClick={() => void saveProject({ ...project, status: "archived" }).then((saved) => saved && navigate("/library"))}>Move to Archived works</button>}
          >
            <p>You can restore this work later. Nothing will be permanently deleted.</p>
          </Modal>
        )}
      </main>
    </>
  );
}

function WorkSettings({ projects, saveProject }: { projects: CreativeProject[]; saveProject: (project: CreativeProject) => Promise<boolean> }) {
  const project = useProject(projects);
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [redraftSource, setRedraftSource] = useState<"original" | "current">("current");
  const [settings, setSettings] = useState(() => project ? {
    workType: project.workType,
    originalLanguage: project.originalLanguage,
    assistanceLevel: project.assistanceLevel,
    audience: project.audience,
    tones: project.tones,
    feelings: project.feelings,
    customTone: project.customTone,
    customFeeling: project.customFeeling,
    emotionalDirection: project.emotionalDirection,
    preserveWording: project.preserveWording,
    preserveCulture: project.preserveCulture,
    collaborationAnswers: project.collaborationAnswers,
  } : null);
  if (!project || !settings) return <Navigate to="/library" replace />;
  const update = (changes: Partial<typeof settings>) => setSettings((value) => value ? { ...value, ...changes } : value);
  const changedProject = {
    ...project,
    ...settings,
    tone: [...settings.tones, settings.customTone].filter(Boolean).join(", ") || "Not sure yet",
  };
  const saveOnly = async () => {
    if (await saveProject(changedProject)) setMessage("Settings saved. Your manuscript has not changed.");
  };
  const createAgain = async () => {
    const input: NewProjectDraft = {
      ...emptyProjectDraft(),
      ...settings,
      idea: redraftSource === "original" ? project.originalInput : project.workingText,
      stage: "review",
      savedAt: new Date().toISOString(),
    };
    const text = await localAssistance.createDraft(input);
    const versions = [...project.versions];
    if (versions.at(-1)?.text !== project.workingText) {
      versions.push({
        id: crypto.randomUUID(),
        number: versions.length + 1,
        name: "Before settings change",
        text: project.workingText,
        createdAt: new Date().toISOString(),
        reason: "saved",
      });
    }
    versions.push({
      id: crypto.randomUUID(),
      number: versions.length + 1,
      name: "Draft from updated settings",
      text,
      createdAt: new Date().toISOString(),
      reason: "continued",
      basedOnVersionId: versions.at(-1)?.id,
    });
    if (await saveProject({ ...changedProject, workingText: text, versions })) navigate(`/project/${project.id}/edit`);
  };
  return (
    <>
      <BrandHeader backTo={`/project/${project.id}`} />
      <main className="page settings-page">
        <section className="page-heading"><p className="eyebrow">Nothing changes without your choice</p><h1>Work settings</h1><p>Change how Author Studio helps and how this work should feel. Saving settings alone will not alter the manuscript.</p></section>
        <fieldset><legend>What kind of work is this?</legend><div className="pill-grid">{(["Fiction", "Non-fiction", "Children's story", "Not sure yet"] as const).map((item) => <TogglePill key={item} label={item} selected={settings.workType === item} onClick={() => update({ workType: item })} />)}</div></fieldset>
        <fieldset><legend>Original language</legend><div className="pill-grid"><TogglePill label="සිංහල — Sinhala" selected={settings.originalLanguage === "si-LK"} onClick={() => update({ originalLanguage: "si-LK" })} /><TogglePill label="English" selected={settings.originalLanguage === "en-GB"} onClick={() => update({ originalLanguage: "en-GB" })} /><TogglePill label="Not sure yet" selected={settings.originalLanguage === "und"} onClick={() => update({ originalLanguage: "und" })} /></div></fieldset>
        <fieldset><legend>How should Author Studio help?</legend><div className="stacked-choices">{(["Keep my words", "Help me improve it", "Develop my ideas", "Create a first draft", "Build it with me"] as AssistanceLevel[]).map((item) => <Choice key={item} selected={settings.assistanceLevel === item} title={item} description="" onClick={() => update({ assistanceLevel: item })} />)}</div></fieldset>
        {settings.assistanceLevel === "Build it with me" && (
          <section className="collaboration-questions">
            <h2>Build it with me answers</h2>
            {([
              ["centre", "Who or what is at the centre of the work?"],
              ["change", "What happens or changes?"],
              ["challenge", "What is the main challenge, question, or message?"],
              ["ending", "How would you like it to end?"],
              ["preserveExactly", "What must remain exactly as described?"],
            ] as const).map(([key, label]) => (
              <label className="field-label" key={key}>
                {label}
                <textarea value={settings.collaborationAnswers[key]} onChange={(event) => update({
                  collaborationAnswers: { ...settings.collaborationAnswers, [key]: event.target.value },
                })} />
              </label>
            ))}
          </section>
        )}
        <fieldset><legend>Audience</legend><input className="text-input" aria-label="Audience" value={settings.audience} onChange={(event) => update({ audience: event.target.value })} /></fieldset>
        <fieldset><legend>How should the work sound?</legend><div className="pill-grid">{toneOptions.map((item) => <TogglePill key={item} label={item} selected={settings.tones.includes(item)} onClick={() => update({ tones: toggleValue(settings.tones, item) })} />)}</div><label className="field-label" htmlFor="settings-tone">Add your own tone</label><input id="settings-tone" className="text-input" value={settings.customTone} onChange={(event) => update({ customTone: event.target.value })} /></fieldset>
        <fieldset><legend>How would you like readers to feel?</legend><div className="pill-grid">{feelingOptions.map((item) => <TogglePill key={item} label={item} selected={settings.feelings.includes(item)} onClick={() => update({ feelings: toggleValue(settings.feelings, item) })} />)}</div><label className="field-label" htmlFor="settings-feeling">Add your own reader feeling</label><input id="settings-feeling" className="text-input" value={settings.customFeeling} onChange={(event) => update({ customFeeling: event.target.value })} /></fieldset>
        <label className="field-label" htmlFor="settings-direction">Describe the feeling in your own words</label><textarea id="settings-direction" className="direction-area" value={settings.emotionalDirection} onChange={(event) => update({ emotionalDirection: event.target.value })} />
        <div className="preserve-options"><strong>Keep what matters</strong><label><input type="checkbox" checked={settings.preserveWording} onChange={(event) => update({ preserveWording: event.target.checked })} /> Keep my original wording where possible</label><label><input type="checkbox" checked={settings.preserveCulture} onChange={(event) => update({ preserveCulture: event.target.checked })} /> Keep cultural words and expressions</label></div>
        <fieldset>
          <legend>What should the new draft use?</legend>
          <div className="pill-grid">
            <TogglePill label="Use my current manuscript" selected={redraftSource === "current"} onClick={() => setRedraftSource("current")} />
            <TogglePill label="Use my original idea" selected={redraftSource === "original"} onClick={() => setRedraftSource("original")} />
          </div>
          <p>Your current manuscript is selected by default. Neither source will be overwritten.</p>
        </fieldset>
        {message && <p role="status" className="notice-message">{message}</p>}
        <div className="wizard-actions split-actions"><button className="button secondary" onClick={() => void saveOnly()}>Save settings only</button><button className="button primary" onClick={() => void createAgain()}>Create a new draft using these settings</button></div>
      </main>
    </>
  );
}

function Editor({ projects, saveMessage, saveProject }: { projects: CreativeProject[]; saveMessage: string; saveProject: (project: CreativeProject) => Promise<boolean> }) {
  const project = useProject(projects);
  const [text, setText] = useState(project?.workingText ?? "");
  const [showHelp, setShowHelp] = useState(new URLSearchParams(location.search).get("new") === "true");
  const [showVersionDialog, setShowVersionDialog] = useState(false);
  const [versionName, setVersionName] = useState("");
  if (!project) return <Navigate to="/library" replace />;

  const changeText = (value: string) => {
    setText(value);
    void saveProject({ ...project, workingText: value });
  };

  const saveVersion = () => {
    const name = versionName.trim() || `Version ${project.versions.length + 1}`;
    const version: ManuscriptVersion = {
      id: crypto.randomUUID(),
      number: project.versions.length + 1,
      name,
      text,
      createdAt: new Date().toISOString(),
      reason: "saved",
    };
    void saveProject({ ...project, workingText: text, versions: [...project.versions, version] });
    setShowVersionDialog(false);
    setVersionName("");
  };

  return (
    <>
      <BrandHeader backTo={`/project/${project.id}`} saveMessage={saveMessage} />
      <main className="editor-page">
        <aside className="editor-sidebar">
          <span className="tag">Master manuscript</span>
          <h1>{project.title}</h1>
          <p>{languageName(project.originalLanguage)} original</p>
          <nav aria-label="Manuscript actions">
            <button className="sidebar-action active"><PenLine /> Writing</button>
            <Link className="sidebar-action" to={`/project/${project.id}/versions`}><Clock3 /> Earlier versions</Link>
            <Link className="sidebar-action" to={`/project/${project.id}/editions`}><Languages /> Language editions</Link>
            <Link className="sidebar-action" to={`/project/${project.id}/listen`}><Headphones /> Listen</Link>
            <Link className="sidebar-action" to={`/project/${project.id}/storyboard`}><BookOpen /> Comic storyboard</Link>
            <Link className="sidebar-action" to={`/project/${project.id}/settings`}><Sparkles /> Work settings</Link>
          </nav>
          <button className="button primary full" onClick={() => setShowVersionDialog(true)}><Save /> Save a new version</button>
        </aside>
        <section className="writing-desk">
          {showHelp && (
            <aside className="assistance-note">
              <Sparkles />
              <div><strong>Your demonstration draft is ready to review</strong><p>This early version creates a demonstration draft using your ideas and settings. Review and change it before treating it as finished work. Your original contribution remains preserved.</p></div>
              <button aria-label="Dismiss explanation" onClick={() => setShowHelp(false)}>×</button>
            </aside>
          )}
          <div className="writing-toolbar">
            <div><span className="eyebrow">Current writing</span><h2>Review and shape your draft</h2></div>
            <span className="word-count">{text.trim() ? text.trim().split(/\s+/).length : 0} words</span>
          </div>
          <textarea aria-label="Master manuscript" className="manuscript-editor" value={text} onChange={(e) => changeText(e.target.value)} />
          <div className="original-input">
            <ShieldCheck />
            <div><strong>Your original contribution is preserved</strong><p>{project.originalInput}</p></div>
          </div>
        </section>
        {showVersionDialog && (
          <Modal
            title="Save a new version"
            onClose={() => setShowVersionDialog(false)}
            actions={<button className="button primary" onClick={saveVersion}>Save this version</button>}
          >
            <label className="field-label" htmlFor="version-name">Give this version a short name</label>
            <input id="version-name" className="text-input" value={versionName} onChange={(event) => setVersionName(event.target.value)} placeholder={`Version ${project.versions.length + 1}`} />
            <p>Your earlier versions will remain unchanged.</p>
          </Modal>
        )}
      </main>
    </>
  );
}

function Versions({ projects, saveProject, addProject }: { projects: CreativeProject[]; saveProject: (project: CreativeProject) => Promise<boolean>; addProject: (project: CreativeProject) => Promise<boolean> }) {
  const project = useProject(projects);
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState(project?.versions.at(-1)?.id ?? "");
  const [pendingReason, setPendingReason] = useState<"restored" | "continued" | null>(null);
  if (!project) return <Navigate to="/library" replace />;
  const selected = project.versions.find((version) => version.id === selectedId) ?? project.versions.at(-1)!;

  const createFrom = (reason: "restored" | "continued") => {
    const next: ManuscriptVersion = {
      ...selected,
      id: crypto.randomUUID(),
      number: project.versions.length + 1,
      name: reason === "restored" ? `Restored from version ${selected.number}` : `Continued from version ${selected.number}`,
      createdAt: new Date().toISOString(),
      reason,
      basedOnVersionId: selected.id,
    };
    void saveProject({ ...project, workingText: selected.text, versions: [...project.versions, next] }).then((saved) => saved && navigate(`/project/${project.id}/edit`));
  };

  const makeCopy = () => {
    const copyVersionId = crypto.randomUUID();
    const copy = {
      ...project,
      id: crypto.randomUUID(),
      title: `${project.title} — Copy`,
      workingText: selected.text,
      versions: [{
        ...selected,
        id: copyVersionId,
        number: 1,
        name: `Copied from version ${selected.number}`,
        createdAt: new Date().toISOString(),
        reason: "initial" as const,
        basedOnVersionId: undefined,
      }],
      editions: [],
      storyboards: [],
      status: "active" as const,
      updatedAt: new Date().toISOString(),
    };
    void addProject(copy).then((created) => created && navigate(`/project/${copy.id}`));
  };

  return (
    <>
      <BrandHeader backTo={`/project/${project.id}/edit`} />
      <main className="page">
        <section className="page-heading"><p className="eyebrow">Nothing is overwritten</p><h1>Earlier versions</h1><p>Choose a saved version to read it or continue from it.</p></section>
        <div className="versions-layout">
          <div className="version-list" aria-label="Saved versions">
            {[...project.versions].reverse().map((version) => (
              <button key={version.id} className={`version-item ${selected.id === version.id ? "selected" : ""}`} onClick={() => setSelectedId(version.id)}>
                <span className="version-number">V{version.number}</span>
                <span><strong>{version.name}</strong><small>{formatTime(version.createdAt)} · {version.reason}</small></span>
                {selected.id === version.id && <Check />}
              </button>
            ))}
          </div>
          <section className="version-preview">
            <div className="tag-row"><span className="tag">Version {selected.number}</span>{selected.basedOnVersionId && <span className="tag soft">Based on an earlier version</span>}</div>
            <h2>{selected.name}</h2>
            <p className="version-date">Saved {formatTime(selected.createdAt)}</p>
            <div className="preview-paper">{selected.text}</div>
            <div className="version-actions">
              <button className="button primary" onClick={() => setPendingReason("restored")}><RotateCcw /> Restore this version</button>
              <button className="button secondary" onClick={() => setPendingReason("continued")}><PenLine /> Continue from here</button>
              <button className="button ghost" onClick={makeCopy}><FileText /> Make a copy</button>
            </div>
            <p className="safety-copy"><ShieldCheck /> These actions create something new. Your existing timeline stays complete.</p>
          </section>
        </div>
        {pendingReason && (
          <Modal
            title={pendingReason === "restored" ? `Restore version ${selected.number}?` : `Continue from version ${selected.number}?`}
            onClose={() => setPendingReason(null)}
            actions={<button className="button primary" onClick={() => createFrom(pendingReason)}>{pendingReason === "restored" ? "Restore as newest version" : "Continue from here"}</button>}
          >
            <p>A new latest version will be created. No saved version will be removed.</p>
          </Modal>
        )}
      </main>
    </>
  );
}

function Editions({ projects, saveProject }: { projects: CreativeProject[]; saveProject: (project: CreativeProject) => Promise<boolean> }) {
  const project = useProject(projects);
  const [selectedId, setSelectedId] = useState(project?.editions[0]?.id ?? "");
  const [showCreator, setShowCreator] = useState(false);
  const [beginning, setBeginning] = useState<"empty" | "copy-source" | "paste">("empty");
  const [pastedText, setPastedText] = useState("");
  if (!project) return <Navigate to="/library" replace />;
  const selected = project.editions.find((edition) => edition.id === selectedId);
  const target: Language = project.originalLanguage === "si-LK" ? "en-GB" : "si-LK";
  const existingTarget = project.editions.find((edition) => edition.language === target);

  const addEdition = async () => {
    if (existingTarget) {
      setSelectedId(existingTarget.id);
      return;
    }
    const latest = project.versions.at(-1)!;
    const created = await localLanguageEditions.createEdition(project, target, beginning, pastedText);
    const edition = {
      id: crypto.randomUUID(),
      language: target,
      label: `${languageName(target)} edition`,
      text: created.text,
      sourceVersionId: latest.id,
      approach: created.approach,
    };
    const saved = await saveProject({ ...project, editions: [...project.editions, edition] });
    if (!saved) return;
    setSelectedId(edition.id);
    setShowCreator(false);
    setPastedText("");
  };

  return (
    <>
      <BrandHeader backTo={`/project/${project.id}`} />
      <main className="page">
        <section className="page-heading split-heading">
          <div>
            <p className="eyebrow">Linked, never overwritten</p>
            <h1>Language editions</h1>
            <p>Each editable edition stays connected to the master version it came from.</p>
          </div>
          <button className="button primary" onClick={() => existingTarget ? setSelectedId(existingTarget.id) : setShowCreator(true)}>
            {existingTarget ? <Languages /> : <Plus />}
            {existingTarget ? `Open ${languageName(target)} edition` : `Create an editable ${languageName(target)} edition`}
          </button>
        </section>
        <aside className="assistance-note">
          <Languages />
          <div><strong>Write this edition in your own way</strong><p>Automatic translation is not available in this early version. You can create an editable language edition and write, copy, or paste translated content.</p></div>
        </aside>
        <div className="edition-map">
          <article className="edition-card master">
            <span className="edition-icon"><FileText /></span>
            <div><span className="tag">Master manuscript</span><h2>{languageName(project.originalLanguage)} original</h2><p>Latest saved version: {project.versions.at(-1)?.name}</p></div>
          </article>
          <div className="edition-connector"><span>Linked editions</span></div>
          <div className="edition-list">
            {project.editions.map((edition) => (
              <button key={edition.id} className={`edition-card ${selectedId === edition.id ? "selected" : ""}`} onClick={() => setSelectedId(edition.id)}>
                <span className="edition-icon"><Languages /></span>
                <div><span className="tag soft">{edition.approach}</span><h2>{edition.label}</h2><p>From master version {project.versions.find((version) => version.id === edition.sourceVersionId)?.number}</p></div>
                <ChevronRight />
              </button>
            ))}
            {!project.editions.length && <div className="empty-state"><Languages /><h2>No language edition yet</h2><p>Create an editable edition when you are ready. The master manuscript will remain unchanged.</p></div>}
          </div>
        </div>
        {selected && (
          <section className="edition-preview">
            <div>
              <span className="tag">{selected.label}</span>
              <h2>Edit this edition</h2>
              <p>{selected.approach === "Copied source"
                ? "This began as a copy of the source. Copying is not translation."
                : selected.approach === "Author-supplied translation"
                  ? "This contains translated writing supplied by the author. Review and edit it as needed."
                  : selected.approach === "Sample content"
                    ? "This is demonstration sample content, not an automatic translation of your manuscript."
                    : "This edition is independent from the source manuscript."}</p>
            </div>
            <textarea aria-label={`${selected.label} text`} value={selected.text} onChange={(e) => void saveProject({ ...project, editions: project.editions.map((edition) => edition.id === selected.id ? { ...edition, text: e.target.value } : edition) })} />
          </section>
        )}
        {showCreator && (
          <Modal
            title="How would you like to begin this edition?"
            onClose={() => setShowCreator(false)}
            actions={<button className="button primary" disabled={beginning === "paste" && !pastedText.trim()} onClick={() => void addEdition()}>Create editable edition</button>}
          >
            <div className="stacked-choices">
              <Choice selected={beginning === "empty"} title="Start with an empty edition" description="Begin with a blank editable page." onClick={() => setBeginning("empty")} />
              <Choice selected={beginning === "copy-source"} title="Copy the source text" description="Copy the current manuscript into the new edition as a starting point. This does not translate it." onClick={() => setBeginning("copy-source")} />
              <Choice selected={beginning === "paste"} title="Paste translated writing" description="Paste translated text that you already have." onClick={() => setBeginning("paste")} />
            </div>
            {beginning === "paste" && (
              <>
                <label className="field-label" htmlFor="translated-writing">Translated writing</label>
                <textarea id="translated-writing" value={pastedText} onChange={(event) => setPastedText(event.target.value)} />
              </>
            )}
            <p>The edition will be linked to the current saved manuscript version. Your source manuscript will not change.</p>
          </Modal>
        )}
      </main>
    </>
  );
}

function Listen({ projects }: { projects: CreativeProject[] }) {
  const project = useProject(projects);
  const [state, setState] = useState<"stopped" | "playing" | "paused">("stopped");
  const [selectedText, setSelectedText] = useState<"master" | string>("master");
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [voiceName, setVoiceName] = useState("");
  const edition = project?.editions.find((item) => item.id === selectedText);
  const text = edition?.text ?? project?.workingText ?? "";
  const language = edition?.language ?? project?.originalLanguage ?? "en-GB";
  const supported = typeof window.speechSynthesis !== "undefined";

  useEffect(() => {
    if (!supported) return;
    const loadVoices = () => setVoices(window.speechSynthesis.getVoices());
    loadVoices();
    window.speechSynthesis.addEventListener("voiceschanged", loadVoices);
    return () => {
      window.speechSynthesis.removeEventListener("voiceschanged", loadVoices);
      window.speechSynthesis.cancel();
    };
  }, [supported]);

  useEffect(() => {
    const matching = voices.find((voice) => voice.lang.toLowerCase().startsWith(language.split("-")[0].toLowerCase()));
    setVoiceName(matching?.name ?? voices[0]?.name ?? "");
  }, [language, voices]);

  const play = () => {
    if (!supported) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = language;
    utterance.voice = voices.find((voice) => voice.name === voiceName) ?? null;
    utterance.onend = () => setState("stopped");
    window.speechSynthesis.speak(utterance);
    setState("playing");
  };
  const pause = () => {
    if (state === "playing") {
      window.speechSynthesis.pause();
      setState("paused");
    } else {
      window.speechSynthesis.resume();
      setState("playing");
    }
  };
  const stop = () => {
    window.speechSynthesis.cancel();
    setState("stopped");
  };

  if (!project) return <Navigate to="/library" replace />;

  return (
    <>
      <BrandHeader backTo={`/project/${project.id}`} />
      <main className="listen-page">
        <section className="listen-card">
          <div className="listen-art"><div className={`sound-orb ${state === "playing" ? "active" : ""}`}><Volume2 /></div></div>
          <div className="listen-content">
            <p className="eyebrow">Listen to my work</p>
            <h1>{project.title}</h1>
            <label className="field-label" htmlFor="listen-source">Choose what to listen to</label>
            <select id="listen-source" value={selectedText} onChange={(e) => { stop(); setSelectedText(e.target.value); }}>
              <option value="master">{languageName(project.originalLanguage)} master manuscript</option>
              {project.editions.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
            </select>
            {supported && voices.length > 0 && (
              <>
                <label className="field-label" htmlFor="listen-voice">Choose a voice</label>
                <select id="listen-voice" value={voiceName} onChange={(event) => setVoiceName(event.target.value)}>
                  {voices.map((voice) => <option key={`${voice.name}-${voice.lang}`} value={voice.name}>{voice.name} ({voice.lang})</option>)}
                </select>
              </>
            )}
            <p className="listen-excerpt">{text.slice(0, 220)}…</p>
            <div className="player-controls">
              <button className="player-main" aria-label={state === "stopped" ? "Play" : "Stop"} onClick={state === "stopped" ? play : stop}>{state === "stopped" ? <Play /> : <Square />}</button>
              <button className="player-secondary" aria-label={state === "paused" ? "Resume" : "Pause"} onClick={pause} disabled={state === "stopped"}>{state === "paused" ? <Play /> : <Pause />}</button>
              <span role="status">{state === "playing" ? "Reading aloud…" : state === "paused" ? "Paused" : "Ready to listen"}</span>
            </div>
            {!supported && <p className="compatibility-note">Reading aloud is not available in this browser. Your writing is still safe and available to read.</p>}
            {supported && voices.length === 0 && <p className="compatibility-note">No reading voices are available on this device. Your writing remains available to read on screen.</p>}
            {language === "si-LK" && <p className="compatibility-note">A Sinhala voice may not be installed on this device. You can choose the English edition or read the text on screen.</p>}
            <p className="privacy-note"><ShieldCheck /> Your writing is read by this device and is not uploaded.</p>
          </div>
        </section>
      </main>
    </>
  );
}

function Storyboard({ projects, saveProject }: { projects: CreativeProject[]; saveProject: (project: CreativeProject) => Promise<boolean> }) {
  const project = useProject(projects);
  const [removePanelId, setRemovePanelId] = useState<string | null>(null);
  if (!project) return <Navigate to="/library" replace />;
  const storyboard = project.storyboards[0];

  const saveStoryboard = (nextStoryboard: NonNullable<typeof storyboard>) => {
    void saveProject({
      ...project,
      storyboards: project.storyboards.map((item) => item.id === nextStoryboard.id ? nextStoryboard : item),
    });
  };

  const updatePanel = (sceneId: string, panelId: string, field: "visual" | "narration" | "speaker" | "dialogue", value: string) => {
    if (!storyboard) return;
    saveStoryboard({
      ...storyboard,
      scenes: storyboard.scenes.map((scene) => scene.id === sceneId ? {
        ...scene,
        panels: scene.panels.map((panel) => panel.id === panelId ? { ...panel, [field]: value } : panel),
      } : scene),
    });
  };

  const addPanel = (sceneId: string) => {
    if (!storyboard) return;
    saveStoryboard({
      ...storyboard,
      scenes: storyboard.scenes.map((scene) => scene.id === sceneId ? {
        ...scene,
        panels: [...scene.panels, { id: crypto.randomUUID(), visual: "Describe what the reader sees.", narration: "", dialogue: "", speaker: "" }],
      } : scene),
    });
  };

  const movePanel = (sceneId: string, panelIndex: number, direction: -1 | 1) => {
    if (!storyboard) return;
    const scene = storyboard.scenes.find((item) => item.id === sceneId);
    if (!scene) return;
    const target = panelIndex + direction;
    if (target < 0 || target >= scene.panels.length) return;
    const panels = [...scene.panels];
    [panels[panelIndex], panels[target]] = [panels[target], panels[panelIndex]];
    saveStoryboard({ ...storyboard, scenes: storyboard.scenes.map((item) => item.id === sceneId ? { ...item, panels } : item) });
  };

  const removePanel = () => {
    if (!storyboard || !removePanelId) return;
    saveStoryboard({
      ...storyboard,
      scenes: storyboard.scenes.map((scene) => ({ ...scene, panels: scene.panels.filter((panel) => panel.id !== removePanelId) })),
    });
    setRemovePanelId(null);
  };

  const create = async () => {
    const latest = project.versions.at(-1)!;
    await saveProject({
      ...project,
      storyboards: [{
        id: crypto.randomUUID(),
        title: `${project.title} — storyboard`,
        sourceVersionId: latest.id,
        scenes: await simulatedStoryboard.createScenes(project),
      }],
    });
  };

  return (
    <>
      <BrandHeader backTo={`/project/${project.id}`} />
      <main className="page storyboard-page">
        <section className="page-heading split-heading"><div><p className="eyebrow">A separate creative adaptation</p><h1>Comic storyboard</h1><p>Plan scenes and panels without changing your master manuscript.</p></div>{!storyboard && <button className="button primary" onClick={() => void create()}><Sparkles /> Turn this into a comic</button>}</section>
        {!storyboard ? (
          <div className="empty-state large-empty"><BookOpen /><h2>Your storyboard will appear here</h2><p>It will use simple written panel plans. No final artwork will be created.</p></div>
        ) : (
          <>
            <aside className="source-banner"><ShieldCheck /><span><strong>Linked to master version {project.versions.find((version) => version.id === storyboard.sourceVersionId)?.number}</strong><small>Changes here do not change the manuscript.</small></span></aside>
            {storyboard.scenes.map((scene, sceneIndex) => (
              <section className="scene" key={scene.id}>
                <div className="scene-heading"><span>Scene {sceneIndex + 1}</span><div><input className="scene-title-input" aria-label={`Scene ${sceneIndex + 1} title`} value={scene.title} onChange={(event) => saveStoryboard({ ...storyboard, scenes: storyboard.scenes.map((item) => item.id === scene.id ? { ...item, title: event.target.value } : item) })} /><p>{scene.setting} · {scene.emotion}</p></div></div>
                <div className="panel-grid">
                  {scene.panels.map((panel, panelIndex) => (
                    <article className="comic-panel" key={panel.id}>
                      <div className="panel-placeholder"><span>Panel {panelIndex + 1}</span><div className="placeholder-sun" /><div className="placeholder-ground" /></div>
                      <div className="panel-copy">
                        <label>What the reader sees<textarea value={panel.visual} onChange={(event) => updatePanel(scene.id, panel.id, "visual", event.target.value)} /></label>
                        <label>Narration<textarea className="narration" value={panel.narration} onChange={(event) => updatePanel(scene.id, panel.id, "narration", event.target.value)} /></label>
                        <label>Speaker<input value={panel.speaker ?? ""} onChange={(event) => updatePanel(scene.id, panel.id, "speaker", event.target.value)} /></label>
                        <label>Dialogue<textarea value={panel.dialogue ?? ""} onChange={(event) => updatePanel(scene.id, panel.id, "dialogue", event.target.value)} /></label>
                        <div className="panel-actions">
                          <button className="button ghost" disabled={panelIndex === 0} onClick={() => movePanel(scene.id, panelIndex, -1)}>Move earlier</button>
                          <button className="button ghost" disabled={panelIndex === scene.panels.length - 1} onClick={() => movePanel(scene.id, panelIndex, 1)}>Move later</button>
                          <button className="button danger" onClick={() => setRemovePanelId(panel.id)}>Remove panel</button>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
                <button className="button secondary add-panel" onClick={() => addPanel(scene.id)}><Plus /> Add a panel</button>
              </section>
            ))}
            {removePanelId && (
              <Modal title="Remove this panel?" onClose={() => setRemovePanelId(null)} actions={<button className="button danger" onClick={removePanel}>Remove panel</button>}>
                <p>This removes the panel from the storyboard only. The master manuscript remains unchanged.</p>
              </Modal>
            )}
          </>
        )}
      </main>
    </>
  );
}

function ArchivePage({ projects, saveProject }: { projects: CreativeProject[]; saveProject: (project: CreativeProject) => Promise<boolean> }) {
  const archived = projects.filter((project) => project.status === "archived");
  return (
    <>
      <BrandHeader backTo="/library" />
      <main className="page">
        <section className="page-heading"><p className="eyebrow">Recoverable at any time</p><h1>Archived works</h1><p>Nothing here has been permanently deleted.</p></section>
        <div className="archive-list">
          {archived.map((project) => (
            <article className="archive-item" key={project.id}><Archive /><div><h2>{project.title}</h2><p>{project.workType} · {languageName(project.originalLanguage)}</p></div><button className="button secondary" onClick={() => void saveProject({ ...project, status: "active" })}><RotateCcw /> Restore</button></article>
          ))}
          {!archived.length && <div className="empty-state"><Archive /><h2>No archived works</h2><p>Works you archive will stay safely here until you restore them.</p></div>}
        </div>
      </main>
    </>
  );
}

export default App;
